import React, { useCallback, useEffect, useState } from 'react';
import { IoNotificationsOutline } from 'react-icons/io5';
import { notificationService } from '../../services/NotificationService';
import NotificationLoginPopup from './NotificationLoginPopup';
import NotificationPanel from './NotificationPanel';
import './NotificationPanel.css';

function formatDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-AR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

export default function NotificationBell() {
    const [unreadCount, setUnreadCount] = useState(0);
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [loginPopups, setLoginPopups] = useState([]);
    const [loading, setLoading] = useState(false);

    const refreshUnreadCount = useCallback(async () => {
        try {
            const count = await notificationService.getUnreadCount();
            setUnreadCount(Number(count) || 0);
        } catch (error) {
            console.error('Error al obtener notificaciones no leídas:', error);
        }
    }, []);

    const showLoginPopups = useCallback(async () => {
        if (sessionStorage.getItem('showNotificationPopups') !== '1') return;

        sessionStorage.removeItem('showNotificationPopups');

        try {
            const popups = await notificationService.getLoginPopup();
            if (popups.length > 0) {
                setLoginPopups(popups);
            }
        } catch (error) {
            console.error('Error al obtener popups de notificaciones:', error);
        }
    }, []);

    const dismissLoginPopup = useCallback((id) => {
        setLoginPopups((prev) => prev.filter((n) => n.id !== id));
    }, []);

    const loadNotifications = useCallback(async () => {
        setLoading(true);
        try {
            const data = await notificationService.getAll();
            setNotifications(data);
        } catch (error) {
            console.error('Error al cargar notificaciones:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleOpenPanel = async () => {
        setIsPanelOpen(true);
        await loadNotifications();
    };

    const handleClosePanel = async () => {
        setIsPanelOpen(false);
        await refreshUnreadCount();
    };

    const handleMarkAsRead = async (id) => {
        try {
            await notificationService.markAsRead(id);
            setNotifications((prev) =>
                prev.map((n) => (n.id === id ? { ...n, read: true } : n))
            );
            await refreshUnreadCount();
        } catch (error) {
            console.error('Error al marcar notificación como leída:', error);
        }
    };

    const handleMarkAllAsRead = async () => {
        try {
            await notificationService.markAllAsRead();
            setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
            setUnreadCount(0);
        } catch (error) {
            console.error('Error al marcar todas como leídas:', error);
        }
    };

    useEffect(() => {
        const init = async () => {
            await refreshUnreadCount();
            await showLoginPopups();
        };

        init();

        const onLoginSuccess = () => {
            setTimeout(async () => {
                await refreshUnreadCount();
                await showLoginPopups();
            }, 300);
        };

        window.addEventListener('loginSuccess', onLoginSuccess);
        return () => window.removeEventListener('loginSuccess', onLoginSuccess);
    }, [refreshUnreadCount, showLoginPopups]);

    return (
        <>
            <div className="notification-bell-container">
                <button
                    type="button"
                    className="notification-bell-btn"
                    onClick={handleOpenPanel}
                    aria-label="Notificaciones"
                >
                    <IoNotificationsOutline size={22} />
                    {unreadCount > 0 && (
                        <span
                            className={`notification-bell-badge${
                                unreadCount <= 9 ? ' notification-bell-badge--single' : ''
                            }`}
                        >
                            {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                    )}
                </button>
            </div>

            <NotificationLoginPopup
                notifications={loginPopups}
                onDismiss={dismissLoginPopup}
            />

            <NotificationPanel
                isOpen={isPanelOpen}
                onClose={handleClosePanel}
                notifications={notifications}
                loading={loading}
                onMarkAsRead={handleMarkAsRead}
                onMarkAllAsRead={handleMarkAllAsRead}
                formatDate={formatDate}
            />
        </>
    );
}
